/**
 * Shared value formatters for page-registered app-context entries.
 *
 * Values on the wire are pre-rendered strings — the producing page owns
 * summarization, the server never inspects or truncates. These helpers
 * exist so pages converge on the same shapes by convenience; nothing
 * enforces their use.
 */

/** `3` + `node` → `3 nodes`. Naive plural unless `pluralNoun` is given. */
export function formatCount(count: number, noun: string, pluralNoun?: string): string {
  const label = count === 1 ? noun : (pluralNoun ?? `${noun}s`)
  return `${count} ${label}`
}

export interface ListSummaryOptions {
  /** Singular noun for the count prefix. Omit for a bare count. */
  noun?: string
  /** Plural override when the noun does not take a trailing `s`. */
  pluralNoun?: string
  /**
   * Full size when `items` is already a sample. Defaults to `items.length`.
   */
  total?: number
  /** How many items to name verbatim. Default 5. */
  max?: number
  /** Parenthesized detail rendered right after the count, e.g. a histogram. */
  detail?: string
}

/**
 * Count-first list summary that names the leading items verbatim —
 * identifiers are the agent's tool-call anchors, so they are never
 * truncated, only capped in number.
 *
 * `['a','b','c'], { noun: 'node', total: 200, max: 2, detail: 'Tag 180' }`
 * → `200 nodes (Tag 180), first 2: a, b`
 */
export function summarizeList(items: readonly string[], options: ListSummaryOptions = {}): string {
  const { noun, pluralNoun, detail } = options
  const max = options.max ?? 5
  const total = options.total ?? items.length
  const head = noun === undefined ? String(total) : formatCount(total, noun, pluralNoun)
  const withDetail = detail !== undefined && detail !== '' ? `${head} (${detail})` : head

  const sample = items.slice(0, Math.max(0, max))
  if (sample.length === 0) return withDetail
  if (sample.length < total) return `${withDetail}, first ${sample.length}: ${sample.join(', ')}`
  return `${withDetail}: ${sample.join(', ')}`
}

export interface HistogramOptions {
  /** How many distinct buckets to name. Default 5. */
  max?: number
}

/**
 * Frequency summary of repeated labels, densest bucket first, ties broken
 * alphabetically so the rendered value is stable across renders.
 *
 * `['Tag','Tag','Equipment']` → `Tag 2, Equipment 1`
 */
export function summarizeHistogram(
  labels: readonly string[],
  options: HistogramOptions = {}
): string {
  const max = options.max ?? 5
  const counts = new Map<string, number>()
  for (const label of labels) {
    if (label === '') continue
    counts.set(label, (counts.get(label) ?? 0) + 1)
  }
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
  const named = ranked.slice(0, max).map(([label, count]) => `${label} ${count}`)
  const remaining = ranked.length - named.length
  if (remaining > 0) named.push(`+${remaining} more`)
  return named.join(', ')
}

/** A labelled qualifier on an entity, e.g. `['severity', 'high']`. */
export type EntityField = readonly [label: string, value: string | number | null | undefined]

export interface EntityDescription {
  /** Display name / title. Omitted when the page has not loaded it yet. */
  name?: string | null
  /** Qualifiers in the order they should read, e.g. status then severity. */
  fields?: readonly EntityField[]
  /** Identifier, rendered last and verbatim — the agent's tool-call anchor. */
  id?: string | null
}

/**
 * The §7 enrichment rule in one place: when a page has the entity loaded,
 * emit `name + status + id` rather than the bare id the URL carries.
 *
 * `{ name: 'Bearing wear', fields: [['severity', 'high']], id: 'abc' }`
 * → `Bearing wear — severity: high, id: abc`
 */
export function describeEntity(entity: EntityDescription): string {
  const qualifiers: string[] = []
  for (const [label, value] of entity.fields ?? []) {
    if (value === null || value === undefined) continue
    const rendered = typeof value === 'number' ? String(value) : value.trim()
    if (rendered === '') continue
    qualifiers.push(`${label}: ${rendered}`)
  }
  const id = entity.id?.trim()
  if (id !== undefined && id !== '') qualifiers.push(`id: ${id}`)

  const name = entity.name?.trim()
  if (name === undefined || name === '') return qualifiers.join(', ')
  if (qualifiers.length === 0) return name
  return `${name} — ${qualifiers.join(', ')}`
}

/**
 * Render a set of active filters as one entry value, dropping the ones
 * left at their default. Returns `none` when nothing is narrowed, so the
 * agent can tell "unfiltered" from "the page forgot to say".
 */
export function describeFilters(filters: readonly EntityField[]): string {
  const parts: string[] = []
  for (const [label, value] of filters) {
    if (value === null || value === undefined) continue
    const rendered = typeof value === 'number' ? String(value) : value.trim()
    if (rendered === '') continue
    parts.push(`${label}: ${rendered}`)
  }
  return parts.length === 0 ? 'none' : parts.join(', ')
}
